import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import PermissionGuard from "@/permissions/PermissionGuard";
import { useVisibleTabs } from "../hooks/useVisibleTabs";

export const CandidateTabs = ({ activeTab, setActiveTab, storedTabIds }) => {
  // `storedTabIds` comes from the parent, which owns the candidate payload.
  // Both callers must pass the same value or the button row and the panel
  // behind it would disagree about which tabs exist.
  const tabs = useVisibleTabs(storedTabIds);
  const tabsRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;

    setScrollState({
      left: tabs.scrollLeft > 0,
      right: tabs.scrollLeft + tabs.clientWidth < tabs.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(tabs);
    tabs.addEventListener("scroll", updateScrollState);

    return () => {
      observer.disconnect();
      tabs.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollTabs = (direction) => {
    tabsRef.current?.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  const hasOverflow = scrollState.left || scrollState.right;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      {hasOverflow && (
        <Button
          type="button"
          aria-label="Show previous candidate tabs"
          onClick={() => scrollTabs(-1)}
          disabled={!scrollState.left}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md p-0 text-text-muted hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeft size={16} />
        </Button>
      )}

      <nav
        ref={tabsRef}
        aria-label="Candidate sections"
        className="scrollbar-hide flex min-w-0 flex-1 items-center gap-1 overflow-x-auto py-1"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <div key={tab.id} className="relative shrink-0">
              <PermissionGuard permissions={tab.permissions} isComponent>
                <Button
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={isActive ? "page" : undefined}
                  className={`
                    relative flex items-center gap-1.5 rounded-lg px-3 py-1.5
                    text-sm font-medium transition-colors duration-150 cursor-pointer
                    ${isActive
                      ? "bg-button-secondary/20 text-button-secondary shadow-sm"
                      : "text-text-muted hover:bg-background-tertiary/60 hover:text-button-secondary"
                    }
                  `}
                >
                  <tab.icon
                    className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-button-secondary" : ""}`}
                    aria-hidden="true"
                  />
                  <span className="whitespace-nowrap">{tab.label}</span>
                </Button>
              </PermissionGuard>
            </div>
          );
        })}
      </nav>

      {hasOverflow && (
        <Button
          type="button"
          aria-label="Show more candidate tabs"
          onClick={() => scrollTabs(1)}
          disabled={!scrollState.right}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md p-0 text-text-muted hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronRight size={16} />
        </Button>
      )}
    </div>
  );
};
